import * as React from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import type { Id } from '@/convex';
import { useOrgContext } from './useOrgContext';
import { useProjectContext } from './useProjectContext';

export interface Binding {
  _id: Id<'projectRepoBindings'>;
  projectId: Id<'projects'>;
  repoFullName: string;
  repoOwner: string;
  repoName: string;
  isPrimary: boolean;
  storybookBaseUrl?: string;
  storybookSource?: 'url' | 'hosted';
  storybookDetected?: boolean;
  storybookFramework?: string;
}

export interface BindingFilterState {
  selectedBindingId: Id<'projectRepoBindings'> | undefined;
  selectedBinding: Binding | undefined;
  linkedDsBinding: Binding | undefined;
  bindings: Binding[];
  isSingleBinding: boolean;
  setBinding: (bindingId: string) => void;
  projectId: Id<'projects'> | undefined;
  isLoading: boolean;
}

export function useBindingFilter(): BindingFilterState {
  const { isLoading: orgLoading } = useOrgContext();
  const { selectedProjectId, isLoading: projectLoading } = useProjectContext();
  const [manualBindingId, setManualBindingId] = React.useState<string>();

  const rawBindings = useQuery(
    api.projects.listBindingsByProject,
    selectedProjectId ? { projectId: selectedProjectId } : 'skip',
  );

  const bindings = React.useMemo(
    () => (rawBindings ?? []) as Binding[],
    [rawBindings],
  );

  const resolvedBindingId = React.useMemo(() => {
    if (bindings.length === 0) return undefined;

    if (manualBindingId) {
      const manualMatch = bindings.find(
        (binding) => String(binding._id) === manualBindingId,
      );
      if (manualMatch) {
        return manualMatch._id;
      }
    }

    const primary = bindings.find((binding) => binding.isPrimary);
    if (primary) return primary._id;

    return bindings[0]._id;
  }, [bindings, manualBindingId]);

  const selectedBinding = React.useMemo(
    () => bindings.find((binding) => binding._id === resolvedBindingId),
    [bindings, resolvedBindingId],
  );

  const setBinding = React.useCallback((bindingId: string) => {
    setManualBindingId(bindingId);
  }, []);

  return {
    selectedBindingId: resolvedBindingId,
    selectedBinding,
    linkedDsBinding: undefined,
    bindings,
    isSingleBinding: bindings.length <= 1,
    setBinding,
    projectId: selectedProjectId,
    isLoading:
      orgLoading ||
      projectLoading ||
      (!!selectedProjectId && rawBindings === undefined),
  };
}
